import React from 'react';
import { OrganizationQuotaDetails, Organization } from '../../../types/domain';
import { formatBytes } from '../types';
import {
  Inbox,
  Database,
  Users as UsersIcon,
  HardDrive,
} from 'lucide-react';

interface OrganizationMetersProps {
  quota: OrganizationQuotaDetails | null;
  currentOrg: Organization | null;
  membersCount: number;
  cardBgClass?: string;
}

export const OrganizationMeters: React.FC<OrganizationMetersProps> = ({
  quota,
  currentOrg,
  membersCount,
  cardBgClass = 'bg-cf-surface',
}) => {
  const subLimit = quota?.submissions?.limit ?? currentOrg?.max_monthly_submissions ?? 1000000;
  const subUsed = quota?.submissions?.used ?? 0;
  const subPct = subLimit > 0 ? Math.min(100, Math.round((subUsed / subLimit) * 100)) : 0;

  const tabLimit = quota?.tables?.limit ?? currentOrg?.max_tables ?? 50;
  const tabUsed = quota?.tables?.used ?? 0;
  const tabPct = tabLimit > 0 ? Math.min(100, Math.round((tabUsed / tabLimit) * 100)) : 0;

  const seatLimit = quota?.seats?.limit ?? currentOrg?.max_team_seats ?? 50;
  const seatUsed = quota?.seats?.used ?? (membersCount > 0 ? membersCount : 1);
  const seatPct = seatLimit > 0 ? Math.min(100, Math.round((seatUsed / seatLimit) * 100)) : 0;

  const storageLimit = quota?.storage?.limit_bytes ?? currentOrg?.max_storage_bytes ?? 104857600;
  const storageUsed = quota?.storage?.used_bytes ?? 0;
  const storagePct = storageLimit > 0 ? Math.min(100, Math.round((storageUsed / storageLimit) * 100)) : 0;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      {/* Meter 1: Submissions */}
      <div className={`p-3 ${cardBgClass} border border-cf-border rounded-lg space-y-2`}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-cf-textSecondary font-medium flex items-center gap-1.5">
            <Inbox size={13} className="text-cf-textMuted" />
            <span>Submissions</span>
          </span>
          <span className="font-semibold text-cf-textPrimary font-mono">
            {subUsed.toLocaleString()} / {subLimit.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              subPct >= 90
                ? 'bg-rose-500'
                : subPct >= 75
                ? 'bg-amber-500'
                : 'bg-cf-orange'
            }`}
            style={{ width: `${subPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-cf-textMuted">
          <span>Monthly Volume</span>
          <span>{subPct}%</span>
        </div>
      </div>

      {/* Meter 2: Datastores */}
      <div className={`p-3 ${cardBgClass} border border-cf-border rounded-lg space-y-2`}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-cf-textSecondary font-medium flex items-center gap-1.5">
            <Database size={13} className="text-cf-textMuted" />
            <span>Datastores</span>
          </span>
          <span className="font-semibold text-cf-textPrimary font-mono">
            {tabUsed.toLocaleString()} / {tabLimit.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              tabPct >= 90
                ? 'bg-rose-500'
                : tabPct >= 75
                ? 'bg-amber-500'
                : 'bg-cf-orange'
            }`}
            style={{ width: `${tabPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-cf-textMuted">
          <span>Datastore Tables</span>
          <span>{tabPct}%</span>
        </div>
      </div>

      {/* Meter 3: Team Seats */}
      <div className={`p-3 ${cardBgClass} border border-cf-border rounded-lg space-y-2`}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-cf-textSecondary font-medium flex items-center gap-1.5">
            <UsersIcon size={13} className="text-cf-textMuted" />
            <span>Team Seats</span>
          </span>
          <span className="font-semibold text-cf-textPrimary font-mono">
            {seatUsed.toLocaleString()} / {seatLimit.toLocaleString()}
          </span>
        </div>
        <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              seatPct >= 90
                ? 'bg-rose-500'
                : seatPct >= 75
                ? 'bg-amber-500'
                : 'bg-cf-orange'
            }`}
            style={{ width: `${seatPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-cf-textMuted">
          <span>Members & Invites</span>
          <span>{seatPct}%</span>
        </div>
      </div>

      {/* Meter 4: Storage */}
      <div className={`p-3 ${cardBgClass} border border-cf-border rounded-lg space-y-2`}>
        <div className="flex items-center justify-between text-xs">
          <span className="text-cf-textSecondary font-medium flex items-center gap-1.5">
            <HardDrive size={13} className="text-cf-textMuted" />
            <span>Storage</span>
          </span>
          <span className="font-semibold text-cf-textPrimary font-mono">
            {quota?.storage?.used_formatted || formatBytes(storageUsed)} /{' '}
            {quota?.storage?.limit_formatted || formatBytes(storageLimit)}
          </span>
        </div>
        <div className="w-full h-1.5 bg-cf-bg border border-cf-border/50 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${
              storagePct >= 90
                ? 'bg-rose-500'
                : storagePct >= 75
                ? 'bg-amber-500'
                : 'bg-cf-orange'
            }`}
            style={{ width: `${storagePct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-cf-textMuted">
          <span>File Attachments</span>
          <span>{storagePct}%</span>
        </div>
      </div>
    </div>
  );
};
